/**
 * Orders Copilot Readable State
 * Exposes order data to AI assistant
 * Uses: store/selectors (reuses existing selectors)
 */

import { useMemo } from 'react'
import { useCopilotReadable } from '@copilotkit/react-core'
import { useSelector } from '@spcc/core'
import type { Order } from '../domain/types'
import {
  selectOrders,
  selectOrdersCount,
  selectPendingOrdersCount,
  selectOrderStats,
  selectRecentOrders,
} from '../store/orders.selectors'

/**
 * Register orders data as readable context for Copilot
 * Call this in components that need AI to understand orders
 */
export const useOrdersReadable = () => {
  const orders = useSelector(selectOrders)
  const count = useSelector(selectOrdersCount)
  const pendingCount = useSelector(selectPendingOrdersCount)
  const stats = useSelector(selectOrderStats)
  const recentOrders = useSelector(selectRecentOrders)

  const value = useMemo(() => ({
    totalOrders: count,
    pendingOrders: pendingCount,
    statistics: {
      pending: stats.pending,
      processing: stats.processing,
      delivered: stats.delivered,
    },
    recentOrders: recentOrders.map(o => ({
      id: o.id,
      orderNumber: o.orderNumber,
      status: o.status,
      totalAmount: `${o.totalAmount.amount} ${o.totalAmount.currency}`,
      deliveryDate: o.deliveryDate,
      products: o.items.map(i => i.product.en).join(', '),
    })),
    allOrders: orders.map(o => ({
      id: o.id,
      orderNumber: o.orderNumber,
      status: o.status,
      totalAmount: `${o.totalAmount.amount} ${o.totalAmount.currency}`,
      orderDate: o.orderDate,
      deliveryDate: o.deliveryDate,
      deliveryAddress: o.deliveryAddress,
      items: o.items.map(i => ({
        product: i.product.en,
        quantity: `${i.quantity.value} ${i.quantity.unit}`,
        price: `${i.totalPrice.amount} ${i.totalPrice.currency}`,
      })),
    })),
  }), [count, pendingCount, stats, recentOrders, orders])

  useCopilotReadable({
    description: 'Current orders in the system with their status and details',
    value,
  })
}

/**
 * Register selected order as readable context
 * Use when viewing order details
 */
export const useSelectedOrderReadable = () => {
  const order = useSelector((state: { orders: { selectedOrder: Order | null } }) =>
    state.orders.selectedOrder
  )

  const value = useMemo(() => order ? {
    id: order.id,
    orderNumber: order.orderNumber,
    status: order.status,
    totalAmount: `${order.totalAmount.amount} ${order.totalAmount.currency}`,
    orderDate: order.orderDate,
    deliveryDate: order.deliveryDate,
    deliveryAddress: order.deliveryAddress,
    notes: order.notes,
    items: order.items.map(i => ({
      product: i.product.en,
      productAr: i.product.ar,
      quantity: `${i.quantity.value} ${i.quantity.unit}`,
      unitPrice: `${i.unitPrice.amount} ${i.unitPrice.currency}`,
      totalPrice: `${i.totalPrice.amount} ${i.totalPrice.currency}`,
    })),
  } : null, [order])

  useCopilotReadable({
    description: 'Currently selected order details',
    value,
  })
}
